/**
 * React Query hook factory for a `ResourceApi` (see resourceApi.ts).
 *
 * Every detail/list screen used to wire its own useQuery + useRealtimeEvents
 * + queryClient.invalidateQueries boilerplate by hand (with all the copy-paste
 * drift that implies — see the WS live-update audit from this session).
 * `createResourceHooks` builds that once per resource: query-key factory,
 * list/detail reads that auto-invalidate on the resource's own WebSocket
 * broadcast, and create/update/remove mutations that invalidate on success.
 *
 * Usage:
 *   export const { useList: useSuppliers, useCreate: useCreateSupplier, ... } =
 *     createResourceHooks(supplierApi, { name: 'suppliers', model: 'warehouses.supplier' });
 */

import { useMemo, useRef } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { useRealtimeEvents } from './useRealtimeEvents';
import type { ListQuery, ResourceApi, ResourceId } from './resourceApi';

export interface ResourceHooksConfig {
  /** Base query-key segment for this resource, e.g. 'suppliers'. */
  name: string;
  /**
   * The broadcast model label whose own pk maps 1:1 to this resource's id
   * (from xsite/core/signals.py's WATCHED_MODELS or simple_history
   * auto-registration), e.g. 'warehouses.supplier'. Drives both list
   * invalidation (any event) and detail invalidation (pk-matched).
   */
  model: string;
  /**
   * Extra related/nested models that should also invalidate the DETAIL query
   * when they fire, e.g. a Contract detail page that embeds its items/staff/
   * vehicles. These are NOT pk-matched (the event's pk is the related row's
   * own id, not this resource's), so any event for them refetches the detail.
   */
  detailRelatedModels?: string[];
  /**
   * Debounce (ms) applied to detail invalidation. Useful when a related model
   * can fire many events in a burst (e.g. a bulk save touching several child
   * rows) — avoids a refetch storm. Default: invalidate immediately.
   */
  detailDebounceMs?: number;
}

export function createResourceHooks<T, TCreate = Partial<T>, TUpdate = Partial<T>>(
  resourceApi: ResourceApi<T, TCreate, TUpdate>,
  config: ResourceHooksConfig
) {
  const keys = {
    all: [config.name] as const,
    lists: () => [...keys.all, 'list'] as const,
    list: (query?: ListQuery) => [...keys.lists(), query ?? {}] as const,
    details: () => [...keys.all, 'detail'] as const,
    detail: (id: ResourceId) => [...keys.details(), id] as const,
  };

  function useList(query?: ListQuery, options?: Omit<UseQueryOptions<T[]>, 'queryKey' | 'queryFn'>) {
    const queryClient = useQueryClient();
    const result = useQuery<T[]>({
      queryKey: keys.list(query),
      queryFn: () => resourceApi.list(query),
      ...options,
    });
    useRealtimeEvents((e) => {
      if (e.model === config.model) queryClient.invalidateQueries({ queryKey: keys.lists() });
    });
    return result;
  }

  function useDetail(id: ResourceId | undefined | null, options?: { enabled?: boolean }) {
    const queryClient = useQueryClient();
    const enabled = (options?.enabled ?? true) && id !== undefined && id !== null;
    const relatedModels = config.detailRelatedModels ?? [];
    const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const result = useQuery<T>({
      queryKey: keys.detail(id as ResourceId),
      queryFn: () => resourceApi.get(id as ResourceId),
      enabled,
    });

    useRealtimeEvents((e) => {
      if (!enabled) return;
      const isPrimary = e.model === config.model;
      if (!isPrimary && !relatedModels.includes(e.model || '')) return;
      if (isPrimary && e.pk !== undefined && String(e.pk) !== String(id)) return;

      const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.detail(id as ResourceId) });
      if (config.detailDebounceMs) {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(invalidate, config.detailDebounceMs);
      } else {
        invalidate();
      }
    });

    return result;
  }

  /**
   * Server-paginated variant of useList, for the "search + ordering + infinite
   * scroll" pattern repeated across most catalog-style screens (Projects,
   * StockInventory, ItemUnitList, ...). `query` should stay referentially
   * stable per distinct filter set (e.g. built with useMemo) since it's part
   * of the query key.
   */
  function useInfiniteList(query?: ListQuery) {
    const queryClient = useQueryClient();
    const result = useInfiniteQuery({
      queryKey: keys.list(query),
      queryFn: ({ pageParam }) => resourceApi.listPage({ ...query, page: pageParam as number }),
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (!lastPage?.next) return undefined;
        try {
          return Number(new URL(lastPage.next).searchParams.get('page')) || undefined;
        } catch {
          return undefined;
        }
      },
    });

    useRealtimeEvents((e) => {
      if (e.model === config.model) queryClient.invalidateQueries({ queryKey: keys.lists() });
    });

    const items = useMemo(
      () => result.data?.pages.flatMap((page) => page.results ?? []) ?? [],
      [result.data]
    );
    const totalCount = result.data?.pages[0]?.count ?? 0;

    return { ...result, items, totalCount };
  }

  function useCreate() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (data: TCreate) => resourceApi.create(data),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.lists() }),
    });
  }

  function useUpdate() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ id, data }: { id: ResourceId; data: TUpdate }) => resourceApi.update(id, data),
      onSuccess: (_result, variables) => {
        queryClient.invalidateQueries({ queryKey: keys.detail(variables.id) });
        queryClient.invalidateQueries({ queryKey: keys.lists() });
      },
    });
  }

  function useRemove() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (id: ResourceId) => resourceApi.remove(id),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.lists() }),
    });
  }

  return { keys, useList, useInfiniteList, useDetail, useCreate, useUpdate, useRemove };
}
